import { useEffect, useState } from 'react';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { IPoliticalPartyAddEditRequest } from '../../../../model/sge/political-party/request/political-party-add-edit-request';
import { IPoliticalPartyResponse } from '../../../../model/sge/political-party/response/political-party-response';
import { hasErrors } from '../../../../utils/validation';
import { toPoliticalPartyForm, validatePoliticalParty } from './political-party-form-utils';

interface PoliticalPartyDrawerProps {
    open: boolean;
    initial?: IPoliticalPartyResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IPoliticalPartyAddEditRequest) => void;
}

export function PoliticalPartyDrawer({ open, initial, isSaving, onClose, onSave }: PoliticalPartyDrawerProps) {
    const [form, setForm] = useState<IPoliticalPartyAddEditRequest>({ isActive: true });
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validatePoliticalParty(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toPoliticalPartyForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IPoliticalPartyAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, name: form.name!.trim(), abbreviation: form.abbreviation!.trim() });
    }

    return (
        <FormDrawer
            open={open}
            icon={<GroupsIcon />}
            title={isEdit ? 'Editar partido político' : 'Novo partido político'}
            subtitle={isEdit ? `${initial?.abbreviation || ''}${initial?.name ? ` · ${initial.name}` : ''}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar partido'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.name || ''} onChange={(e) => update({ name: e.target.value })} error={!!errors.name} />
                        <FieldError message={errors.name} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Sigla</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.abbreviation || ''} onChange={(e) => update({ abbreviation: e.target.value })} error={!!errors.abbreviation} />
                        <FieldError message={errors.abbreviation} />
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField
                            label="Ativo"
                            description="Indica se o partido está em atividade"
                            checked={!!form.isActive}
                            onChange={(isActive) => update({ isActive })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
